<template>
  <div ref="socialShareWidget"
       class="social-share">
    <strong v-if="!isMobile && displayLabel"
            class="social-share__label">
      {{ label }}
    </strong>
    <a v-for="(social, index) in socials"
       :key="`${social.className}_${index}`"
       :href="social.link"
       :class="`social-share__${social.className}`"
       target="_blank"
       rel="noopener">
      <template v-if="social.className === 'twitter'">
        <svg xmlns="http://www.w3.org/2000/svg"
             fill="#fff"
             height="1em"
             viewBox="0 0 512 512">
          <!--! Font Awesome Pro 6.4.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. -->
          <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7
                   275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4
                   421.8z" />
        </svg>
       </template>
       <template v-else>
           <font-awesome-icon :icon="social.icon"></font-awesome-icon>
       </template>
    </a>
  </div>
</template>

<script>
import { breakpoints } from "~/Scripts/mixins/common";

export default {
  name: "SocialShare",
  mixins: [breakpoints],
  props: {
    displayLabel: {
      type: Boolean,
      default: true,
    },
    label: {
      type: String,
      default: "Share",
    },
    defaultPageTitle: {
      type: String,
      default: null,
    },
    pageLink: {
      type: String,
      required: true,
    },
  },
  computed: {
    socials () {
      return [{
        link: this.facebookLink,
        className: "facebook",
        icon: ["fab", "facebook-f"],
      }, {
        link: this.twitterLink,
        className: "twitter",
        icon: ["fab", "twitter"],
      }, {
        link: this.linkedInLink,
        className: "linkedIn",
        icon: ["fab", "linkedin-in"],
      }, {
        link: this.whatsAppLink,
        className: "whatsApp",
        icon: ["fab", "whatsapp"],
      }, {
        link: this.emailLink,
        className: "email",
        icon: ["fal", "envelope"],
      }];
    },
    mainPageHeading () {
      let result = this.defaultPageTitle;

      const headingElement = document.getElementsByTagName( "h1" )[0];
      if ( headingElement ) {
        result = headingElement.innerHTML.trim();
      }
      return result;
    },
    shareTitle () {
      return encodeURI( this.mainPageHeading ).replace( "%20", "+" );
    },
    sharePageLink () {
      return encodeURI( this.pageLink );
    },
    emailLink () {
      return `mailto:?body=${encodeURI( this.mainPageHeading )}%20${this.sharePageLink}`;
    },
    linkedInLink () {
      return `https://www.linkedin.com/sharing/share-offsite/?url=${this.sharePageLink}`;
    },
    facebookLink () {
      return `https://www.facebook.com/sharer/sharer.php?u=${this.sharePageLink}&quote=${this.shareTitle}`;
    },
    twitterLink () {
      return `https://twitter.com/intent/tweet?text=${this.shareTitle}+${this.sharePageLink}`;
    },
    whatsAppLink () {
      return `https://wa.me/?text=${this.shareTitle}+${this.sharePageLink}`;
    },
  },
  watch: {
    isMobile ( val ) {
      if ( val ) {
        this.$refs.socialShareWidget.style.marginTop = "0px";
      } else {
        this.alignWidgetToHeading();
      }
    },
  },
  mounted () {
    if ( !this.isMobile ) {
      this.alignWidgetToHeading();
    }
  },
  methods: {
    alignWidgetToHeading () {
      const mainElement = document.getElementsByTagName( "main" )[0];
      const headingElement = document.getElementsByTagName( "h1" )[0];

      if ( mainElement && headingElement ) {
        // eslint-disable-next-line
        const offset = headingElement.getBoundingClientRect().top - mainElement.getBoundingClientRect().top;
        if ( offset > 0 && this.$refs.socialShareWidget ) {
          this.$refs.socialShareWidget.style.marginTop = `${offset}px`;
        }
      }
    },
  },
};
</script>

<style scoped lang="less">
@import "../../Styles/utils/dimensions.less";

.social-share {
  position: sticky;
  top: 100px;
  display: flex;
  float: right;
  flex-flow: column;
  z-index: 1234567890;

  @media(max-width: @below-tablet) {
    flex-flow: row;
    bottom: 0;
    top: unset;
    width: 100%;
    padding-top: 1rem; // bottom margin in parent container
  }

  &__container {
    position: relative;
  }

  &__label {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 0.875rem;
    padding: 0.25rem;
    @border: 1px solid #313537;
    border-top: @border;
    border-right: @border;
    border-left: @border;
    background-color: #fff;
    opacity: 0.85;
  }

  > a {
    color: #fff;
    text-decoration: none;
    display: flex;
    justify-content: center;
    align-items: center;
    @dims: 1.5rem;
    padding: 1rem;
    font-size: @dims;

    > svg {
      transition: transfrom 0.3s ease-out;
      width: @dims;
      height: @dims;

      &:hover {
        transform: scale(1.2);
        transition: transform 0s;
      }
    }

    @media (max-width: @tablet) {
      @dims: 1rem;
      padding: 1rem;
      font-size: @dims;

      > svg {
        width: @dims;
        height: @dims;
      }
    }

    @media(max-width: @below-tablet) {
      @dims: 1rem;
      padding: 0.5rem;
      font-size: @dims;
      width: unset;
      height: unset;
      flex: 1;

      > svg {
        width: @dims;
        height: @dims;
      }
    }
  }

  &__email {
    background-color: #313537;
  }

  &__twitter {
    background-color: #000000;
  }

  &__whatsApp {
    background-color: #01e675;
  }

  &__facebook {
    background-color: #4C77D8;
  }

  &__linkedIn {
    background-color: #0073b1;
  }
}
</style>

<style lang="less">
/* <social-share /> parent container */
main {
  position: relative;

  @media (max-width: 767px) { // below-tablet (avoid global import)
    padding-bottom: 3rem; // social-share::padding + social-share::svg::$dims + any margin
  }
}
</style>
